import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { DateTime, ExprForm } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import {
  EncFilterField,
  EncTopByFieldCondition,
  EncTopByFieldFilterField,
  FilterType,
  SelectionMode,
  VariableType,
} from '@mode-vegas/vegas';
import { IconComponent, TooltipDelay, TooltipDirective } from '@mode/capra';
import { EncodingHelpers, SwitchSchemaModel } from '@mode/charts/util-vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField, FormulaOperations, Switch } from '@mode/shared/util-switch';
import { ContextMenuComponent, ContextMenuModule, ContextMenuService } from '@perfectmemory/ngx-contextmenu';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { EncodingPillBaseComponent } from '../encoding-field/encoding-pill-base.component';
import * as FieldHelpers from '../helpers/field-helpers';
import { Observable } from 'rxjs';

function isTopNFilterField(field?: EncFilterField): field is EncTopByFieldFilterField {
  return field?.filterType === FilterType.TopByField;
}

@Component({
  standalone: true,
  imports: [CommonModule, ContextMenuModule, EncodingPillBaseComponent, IconComponent, TooltipDirective],
  selector: 'mode-encoding-field-filter',
  templateUrl: './encoding-field-filter.component.html',
  styleUrls: ['./encoding-field-filter.component.scss'],
})
export class EncodingFieldFilterComponent implements OnChanges {
  @Input({ required: true }) field!: EncFilterField;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input({ required: true }) viewName!: string;
  @Input({ required: true }) vizToken!: string;
  @Input() disableAggregate = false;
  @Input() allowContinuous = false;

  @Output() remove = new EventEmitter();
  @Output() editFilter = new EventEmitter<{ encFilterField: EncFilterField; oldFilter: EncFilterField }>();
  @Output() updateVariableType = new EventEmitter<{ field: EncFilterField; type: VariableType }>();
  @Output() updateSequence = new EventEmitter<{
    field: EncTopByFieldFilterField;
    sequence: EncTopByFieldCondition.Sequence;
  }>();

  public weekStartOptions = FieldHelpers.WeekStartOptions;
  public yearStartOptions = FieldHelpers.YearStartOptions;

  public variableDiscrete = VariableType.DISCRETE;
  public variableContinuous = VariableType.CONTINUOUS;

  public TopByFieldSequence = EncTopByFieldCondition.Sequence;

  view: FlamingoTypes.ModeView | undefined;
  schemaModel: SwitchSchemaModel | undefined;
  formula: CompiledField | undefined;

  isValid = false;
  isContinuous = false;
  isDiscrete = false;
  title = '';
  displayName = '';
  errorMessage = '';

  datePart: DateTime.Unit | undefined;
  isDatePart = false;
  isDateTrunc = false;
  isExactDateAndTime = false;
  isDateExpr = false;

  weekStart: DateTime.WeekStart | undefined;
  yearStart: DateTime.YearStart | undefined;
  defaultWeekStart: DateTime.WeekStart | undefined;
  defaultYearStart: DateTime.YearStart | undefined;
  dateTruncOpts: Array<FieldHelpers.DateUnitOpt> = [];
  dateTruncMoreOpts: Array<FieldHelpers.DateUnitOpt> = [];
  datePartOpts: Array<FieldHelpers.DateUnitOpt> = [];
  datePartMoreOpts: Array<FieldHelpers.DateUnitOpt> = [];

  hasVarTypeMenuOpts = false;
  hasTopByFieldOptions = false;

  topByFieldSequence: EncTopByFieldCondition.Sequence | undefined;
  topByFieldTooltipContent: string | undefined;
  topByFieldIcon: string | undefined;

  caretIcon = 'icon-invalid';

  readonly tooltipInteractive = false;
  readonly tooltipAppendTo: Element | undefined;
  readonly tooltipDelay: TooltipDelay = 500;

  @ViewChild(ContextMenuComponent) public menu: ContextMenuComponent | undefined;

  public isCustomCalenderSettingsEnabled$: Observable<boolean>;

  constructor(
    public contextMenuService: ContextMenuService,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.isCustomCalenderSettingsEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.CustomCalendar);
  }

  showContextMenu($event: MouseEvent) {
    this.contextMenuService.show.next({
      // Optional - if unspecified, all context menu components will open
      contextMenu: this.menu,
      event: $event,
      item: this.field,
    });
    $event.preventDefault();
    $event.stopPropagation();
  }

  handleDoubleClick($event: MouseEvent): void {
    $event.stopPropagation();
    this.onEditFilter();
  }

  ngOnChanges() {
    if (this.field == null) {
      return; // component should be cleaning up
    }

    this.view = Switch.Views.find(this.model, this.viewName);
    if (this.view == null) {
      return;
    }
    this.schemaModel = SwitchSchemaModel.make(this.model, this.viewName);
    if (this.schemaModel == null) {
      return;
    }

    const compiled = CompiledField.fromEncField(this.view, this.field);
    this.formula = compiled;
    this.isContinuous = compiled.isContinuous;
    this.isDiscrete = compiled.isDiscrete;
    this.displayName = compiled.displayFormula;

    this.datePart = compiled.dateFuncUnit;
    this.isDatePart = compiled.form === ExprForm.DatePart;
    this.isDateTrunc = compiled.form === ExprForm.DateTrunc;
    this.isExactDateAndTime = compiled.form === ExprForm.Access ? compiled.isAttrDateType : false;
    this.isDateExpr =
      compiled.isAttrDateType || compiled.form === ExprForm.DatePart || compiled.form === ExprForm.DateTrunc;

    const dateTruncMenuOpts = FieldHelpers.buildDateTruncMenuOpts(compiled);
    this.dateTruncOpts = this.isDateExpr ? dateTruncMenuOpts.mainMenuOpts : [];
    this.dateTruncMoreOpts = this.isDateExpr ? dateTruncMenuOpts.moreMenuOpts : [];

    const datePartMenuOpts = FieldHelpers.buildDatePartMenuOpts(compiled);
    this.datePartOpts = this.isDateExpr ? datePartMenuOpts.mainMenuOpts : [];
    this.datePartMoreOpts = this.isDateExpr ? datePartMenuOpts.moreMenuOpts : [];

    this.weekStart = compiled.hasDateFunc ? (compiled.weekStart ?? compiled.defaultWeekStart) : undefined;
    this.yearStart = compiled.hasDateFunc ? (compiled.yearStart ?? compiled.defaultYearStart) : undefined;
    this.defaultYearStart = compiled.defaultYearStart;
    this.defaultWeekStart = compiled.defaultWeekStart;

    const topNFilterField = isTopNFilterField(this.field) ? this.field : undefined;
    const isTopNFilter = topNFilterField != null;

    this.errorMessage = '';
    this.isValid = compiled.isValid;

    if (!compiled.isValid) {
      this.errorMessage = compiled.errors.map((error) => error.message).join('\n');
    }

    if (this.disableAggregate && compiled.isNonScalarCalc) {
      this.isValid = false;
      this.errorMessage = 'Aggregate/Analytic filters are not supported on this visualization type';
    } else if (!this.disableAggregate && compiled.isAnalyticCalc) {
      this.isValid = false;
      this.errorMessage = 'Analytic filters are not yet supported';
    } else if (isTopNFilter) {
      // Validate the field and byField
      const errors = EncodingHelpers.validateFilterField(this.field, this.schemaModel);
      this.isValid = !errors.length;
      if (!this.isValid) {
        this.errorMessage = errors[0];
      }
    }

    if (this.isValid && topNFilterField) {
      this.topByFieldSequence = topNFilterField.cond.sequence ?? EncTopByFieldCondition.Sequence.FirstIsolate;
      this.topByFieldTooltipContent = this.makeTopByFieldTooltipContent(topNFilterField);
      this.topByFieldIcon =
        topNFilterField.cond.operator === EncTopByFieldCondition.Operator.Top ? 'icon-rank-top' : 'icon-rank-bottom';
    } else {
      this.topByFieldSequence = undefined;
      this.topByFieldTooltipContent = undefined;
      this.topByFieldIcon = undefined;
    }

    this.title = !this.isValid
      ? this.errorMessage
      : isTopNFilter
        ? // `undefined` actually shows a title of "undefined"..
          // we want it to not show up in favor of the tooltip
          // and an empty strings accomplishes this
          ''
        : this.displayName;

    this.caretIcon = !this.isValid ? 'icon-invalid' : compiled.isDiscrete ? 'icon-discrete' : 'icon-continuous';

    this.hasVarTypeMenuOpts =
      this.isValid && !compiled.isSystem && (compiled.isAttrNumberType || compiled.isAttrDateType);
    this.hasTopByFieldOptions = this.isValid && isTopNFilter;
  }

  onEditFilter() {
    this.editFilter.emit({
      encFilterField: this.field,
      oldFilter: this.field,
    });
  }

  removeField(): void {
    this.remove.emit({ field: this.field });
  }

  setFormula(formula: string, varType = this.formula?.varType) {
    const newFormula = this.formula?.withNewFormula(formula, varType);
    if (newFormula?.varType === Attr.Type.Continuous) {
      let filterType = FilterType.Continuous;
      if (newFormula.isFormulaDate) {
        filterType = FilterType.ContinuousDate;
      }

      this.editFilter.emit({
        encFilterField: {
          filterType,
          variable: { type: VariableType.CONTINUOUS },
          formula,
          range: { min: null as unknown as number, max: null as unknown as number },
          includeMin: true,
          includeMax: true,
          includeNulls: false,
        },
        oldFilter: this.field,
      });
    } else {
      this.editFilter.emit({
        encFilterField: {
          filterType: FilterType.Discrete,
          variable: { type: VariableType.DISCRETE },
          formula,
          selection: {
            selected: [],
            mode: SelectionMode.ALL,
          },
        },
        oldFilter: this.field,
      });
    }
  }

  setVariableType(type: VariableType) {
    this.updateVariableType.emit({ field: this.field, type: type });
  }

  setSequence(sequence: EncTopByFieldCondition.Sequence) {
    this.updateSequence.emit({ field: this.field as EncTopByFieldFilterField, sequence });
  }

  setDatePart(unit: DateTime.Unit) {
    if (!this.formula) {
      return;
    }

    if (this.isDatePart && this.datePart === unit) {
      return;
    }

    const source = FormulaOperations.datePart(this.formula, unit);
    const varType = this.isDatePart ? this.formula.varType : Attr.Type.Discrete;
    this.setFormula(source, varType);
  }

  setDateTrunc(unit: DateTime.Unit) {
    if (!this.formula) {
      return;
    }

    if (this.isDateTrunc && this.datePart === unit) {
      return;
    }

    const source = FormulaOperations.dateTrunc(this.formula, unit);
    const varType = this.isDateTrunc ? this.formula.varType : Attr.Type.Continuous;
    this.setFormula(source, varType);
  }

  setExactDateTime() {
    if (!this.formula) {
      return;
    }

    if (this.isExactDateAndTime) {
      return;
    }

    const source = FormulaOperations.access(this.formula);
    this.setFormula(source);
  }

  setWeekStart(weekStart: DateTime.WeekStart): void {
    this.setWeekOrYearStart(weekStart, undefined);
  }

  setYearStart(yearStart: DateTime.YearStart): void {
    this.setWeekOrYearStart(this.weekStart, yearStart);
  }

  setWeekOrYearStart(weekStart?: DateTime.WeekStart, yearStart?: DateTime.YearStart): void {
    let source = '';
    let varType;

    if (this.formula?.dateFuncUnit) {
      const dateFunc = this.isDateTrunc ? FormulaOperations.dateTrunc : FormulaOperations.datePart;
      source = dateFunc(this.formula, this.formula.dateFuncUnit, weekStart, yearStart);
      varType = this.formula.varType;
    } else if (this.formula?.isAttrDateType) {
      const dateFuncUnit = weekStart ? DateTime.Unit.Week : DateTime.Unit.Year;
      source = FormulaOperations.dateTrunc(this.formula, dateFuncUnit, weekStart, yearStart);
      varType = this.allowContinuous ? Attr.Type.Continuous : Attr.Type.Discrete;
    }

    this.setFormula(source, varType);
  }

  makeTopByFieldTooltipContent(field: EncTopByFieldFilterField) {
    const dir = field.cond.operator === EncTopByFieldCondition.Operator.Top ? 'top' : 'bottom';
    const seq = field.cond.sequence === EncTopByFieldCondition.Sequence.LastIsolate ? 'last' : 'first';
    return `This ${dir} N filter applies ${seq}`;
  }
}
